<script lang="ts" setup>
/**
 * @link https://idsk.gov.sk/komponenty/formularove-prvky#form-checkboxes
 */
import { CLASS_ROOT } from './constants'

export type IDSKCheckboxesOptionType = {
  id: string
  value: string
  label: string
  dataTestId?: string
}

export interface IDSKCheckboxesProps {
  id?: string
  name?: string
  label?: string
  hint?: string
  hintId?: string
  /** Error message text, also serves as a flag whether the input is valid. */
  errorMessage?: string
  errorMessageId?: string
  options: ReadonlyArray<IDSKCheckboxesOptionType>
  modelValue: ReadonlyArray<string>
  disabledVal?: any
  variant?: 'horizontal' | 'vertical'
}

const props = withDefaults(defineProps<IDSKCheckboxesProps>(), {
  id: undefined,
  label: undefined,
  name: undefined,
  hint: undefined,
  hintId: undefined,
  errorMessage: undefined,
  errorMessageId: undefined,
  disabledVal: undefined,
  variant: 'vertical',
})

const emit = defineEmits(['update:modelValue', 'clickedCheckbox'])

const handleChange = (event: InputEvent, checkboxIndex: number) => {
  if (event.target instanceof HTMLInputElement) {
    const checkboxValue = event.target.value
    const resultSet = new Set([...props.modelValue, checkboxValue])
    if (!event.target.checked) {
      resultSet.delete(checkboxValue)
    }
    emit('clickedCheckbox', checkboxIndex, event.target.value, event.target.checked)
    emit('update:modelValue', Array.from(resultSet))
  }
}
</script>

<template>
  <div
    :class="[
      'govuk-form-group',
      {
        ['govuk-form-group--error']: errorMessage,
      },
    ]"
  >
    <fieldset class="govuk-fieldset">
      <IDSKFieldCaption
        :label="label"
        :hint="hint"
        :hint-id="hintId"
        :error-message="errorMessage"
        :error-message-id="errorMessageId"
        variant="legend"
      />
      <div
        :class="[
          CLASS_ROOT,
          {
            [`${CLASS_ROOT}--horizontal`]: props.variant === 'horizontal',
          },
        ]"
      >
        <IDSKCheckbox
          v-for="option in props.options"
          v-bind="$attrs"
          :id="option.id"
          :key="option.value"
          :value="option.value"
          :disabled="props.disabledVal?.includes(option.id)"
          :name="props.name"
          :checked="modelValue?.includes(option.value)"
          is-in-checkboxes
          :data-test-id="option.dataTestId ? option.dataTestId : option.id"
          @change="(e) => handleChange(e, props.options.indexOf(option))"
        >
          <template #default>{{ option.label }}</template>
          <template v-if="$slots[`${option.id}-visible-content`]" #visible-content>
            <slot :name="`${option.id}-visible-content`" />
          </template>
          <template v-if="$slots[`${option.id}-hidden-content`]" #hidden-content>
            <slot :name="`${option.id}-hidden-content`" />
          </template>
        </IDSKCheckbox>
      </div>
    </fieldset>
  </div>
</template>

<style lang="scss" scoped>
.govuk-checkboxes {
  &--horizontal {
    display: flex;
    flex-direction: row;
  }
}
</style>
